import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { usePlaidTransactions } from "./hooks/usePlaidTransactions";

const formatCurrency = (amount) => {
  if (amount == null) { return "-"; }
  const num = Number(amount);
  const prefix = num < 0 ? "-" : "";
  return prefix + "$" + Math.abs(num).toFixed(2);
};

const PlaidTransactionModal = ({
  open,
  onClose,
  plaidItemId,
  plaidAccountId,
  accountLabel
}) => {
  const { data, isLoading, error } = usePlaidTransactions(
    plaidItemId, plaidAccountId, open
  );

  const plaidTxs = data?.plaidTransactions || [];
  const ourTxs = data?.ourTransactions || [];
  const maxRows = Math.max(plaidTxs.length, ourTxs.length);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        Transactions — {accountLabel}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: 4
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.data?.error ||
              error.message ||
              "Error loading transactions"}
          </Alert>
        )}

        {data?.plaidError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Plaid API error: {data.plaidError}
          </Alert>
        )}

        {data && maxRows === 0 && (
          <Typography color="textSecondary">
            No transactions found on either side.
          </Typography>
        )}

        {data && maxRows > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "50%" }}>
                  <strong>Plaid Transactions (API)</strong>
                </TableCell>
                <TableCell sx={{ width: "50%" }}>
                  <strong>Our Transactions (DB)</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: maxRows }).map((_, i) => {
                const ptx = plaidTxs[i];
                const otx = ourTxs[i];
                return (
                  <TableRow key={i}>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      {ptx
                        ? (
                          <Box>
                            <Typography variant="body2">
                              {ptx.date}{"  "}
                              {ptx.name}{"  "}
                              {formatCurrency(ptx.amount)}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                            >
                              cat: {ptx.category || "-"}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="textSecondary"
                            >
                              plaid_tx_id: {ptx.transactionId}
                            </Typography>
                          </Box>
                        )
                        : (
                          <Typography color="textSecondary">
                            -
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      {otx
                        ? (
                          <Box>
                            <Typography variant="body2">
                              {otx.transactionDate}{"  "}
                              {otx.memo}{"  "}
                              {formatCurrency(otx.amount)}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                            >
                              cat: {otx.joroCategory || "-"}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                            >
                              kgCO2e: {otx.kgCO2e}
                            </Typography>
                          </Box>
                        )
                        : (
                          <Typography color="textSecondary">
                            -
                          </Typography>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlaidTransactionModal;
